/**
 * distributeToTarget: IDI-309 / IDI-361 (P-02, E-09).
 *
 * Re-price a quote's ELIGIBLE lines, ONE time, so the whole-quote figure
 * lands exactly on a target subtotal. This is what the estimate card's Gross
 * Margin % and Bid Subtotal fields do, and IDI-329 reuses it with a computed
 * target.
 *
 * WHY: those fields used to write `overall_margin`. That is the LOWEST tier
 * of the margin engine, so every line with a higher-tier margin overruled it
 * and nothing on screen moved, while the written value showed up later as a
 * phantom "Quote margin X%" (IDI-312). This function writes each eligible
 * line's PRICE (lineTotal, read back by the engine as `price_override`).
 * It never writes lineMargin, categoryMargin or overall_margin. Nothing
 * stays behind as a standing override: editing a re-priced line afterwards
 * drops the cached total (applyLineEdit) and the line goes back to its own
 * margin tier.
 *
 * Eligibility (10-02 ruling):
 *   - Options are excluded (they aren't part of the base bid).
 *   - Lines with a DEALER-SET category margin are excluded: a 40% Fiberglass
 *     Blow line stays at 40%. (IDI-312 removed the seed defaults, so a line
 *     only carries a categoryMargin when a dealer set one.)
 *   - Frozen lines are excluded: every line when the caller reads stamps
 *     (preferStamp), or a line that carries a resolved_margin/margin_source
 *     stamp on a quote that is no longer open (isQuoteRepriceable).
 *   - A line with a per-line margin (lineMargin) IS eligible. Its lineMargin
 *     is kept; only the price is overridden.
 *
 * Arithmetic: fixed = sum of current totals of included, non-eligible lines.
 * Each eligible line's CURRENT total is scaled by (target - fixed) / sum of
 * eligible totals and rounded to cents. The rounding residual goes on the
 * largest eligible line, so eligible + fixed === target to the cent.
 *
 * The isRealPriceOverride trap: when an adjusted total comes within 1 cent of
 * cost / (1 - resolvedMargin), the engine treats it as tier-derived and labels
 * it with that tier instead of `price_override`. The rendered total is still
 * the stored total (computeLineItem returns the stored lineTotal whenever it
 * is > 0), so the sum stays exact. Tests check the rendered totals, not only
 * marginSource.
 *
 * Pure. Never mutates its input.
 */
import { computeLineItem, marginFromPrice, MAX_MARGIN_PCT } from './marginEngine';
import { isOptionLine } from './separateOptions';
import { isQuoteRepriceable } from './repriceOpenQuoteLines';

export const TARGET_REFUSALS = {
  NO_ELIGIBLE: 'no_eligible',
  ALL_CATEGORY_MARGIN: 'all_category_margin',
  NO_ELIGIBLE_TOTAL: 'no_eligible_total',
  BELOW_COST: 'below_cost',
  ABOVE_MAX: 'above_max',
  INVALID: 'invalid_target',
};

function isMarginSet(v) {
  if (v == null || v === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

function isOptionTargetLine(li) {
  return !!li?.optionId || isOptionLine(li);
}

function hasDealerCategoryMargin(li) {
  return isMarginSet(li?.categoryMargin ?? li?.category_margin);
}

function hasStamp(li) {
  const m = li?.resolved_margin ?? li?.resolvedMargin;
  const s = li?.margin_source;
  return m != null && Number.isFinite(Number(m)) && typeof s === 'string' && s !== '';
}

/**
 * Why a single line is (or isn't) re-priceable by a target edit.
 * Returns null when eligible, else a short reason key.
 */
export function lineIneligibility(li, opts = {}) {
  if (!li) return 'empty';
  if (isOptionTargetLine(li)) return 'option';
  if (hasDealerCategoryMargin(li)) return 'category_margin';
  if (opts.preferStamp === true) return 'frozen';
  if (opts.quote && !isQuoteRepriceable(opts.quote) && hasStamp(li)) return 'frozen';
  if (typeof opts.isEligible === 'function' && !opts.isEligible(li)) return 'not_editable';
  return null;
}

function fmtCents(c) {
  const n = Number(c) || 0;
  const abs = Math.abs(n);
  const body = `$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${body}` : body;
}

/**
 * Whether a target edit can do anything on these lines, and why not.
 * The page uses this to render the GM% / Bid Subtotal fields read-only.
 *
 * @returns {{ editable: boolean, reason: string|null, message: string|null }}
 */
export function targetEditability(lines, opts = {}) {
  const safe = Array.isArray(lines) ? lines : [];
  const isIncluded = typeof opts.isIncluded === 'function' ? opts.isIncluded : (li) => !isOptionTargetLine(li);
  const included = safe.filter((li) => li && isIncluded(li));
  const reasons = included.map((li) => lineIneligibility(li, opts));
  if (reasons.some((r) => r === null)) return { editable: true, reason: null, message: null };
  if (included.length > 0 && reasons.every((r) => r === 'category_margin')) {
    return {
      editable: false,
      reason: TARGET_REFUSALS.ALL_CATEGORY_MARGIN,
      message: 'Every line has its own category margin, so there is nothing to re-price from here. Change a category margin in Settings, or edit a line.',
    };
  }
  return {
    editable: false,
    reason: TARGET_REFUSALS.NO_ELIGIBLE,
    message: opts.noEligibleMessage
      || (included.length === 0
        ? 'Add a line item to set a margin or bid subtotal.'
        : 'No line here can be re-priced from the estimate (category-margin, option and locked lines keep their prices).'),
  };
}

function defaultWrite(line, total) {
  return { ...line, lineTotal: total, line_total: total, marginSource: 'price_override' };
}

/**
 * @param {Array} lines - line items, in display order
 * @param {number} targetSubtotalCents - the whole-quote figure to land on
 * @param {object} [opts]
 * @param {object|number} [opts.marginContext] - passed to computeLineItem
 * @param {(line, index) => {total:number, cost:number}} [opts.measure] -
 *   current engine output for a line (defaults to computeLineItem). The quote
 *   builder passes its already-computed lines so accessories etc. are counted
 *   exactly as the card counts them.
 * @param {(line) => boolean} [opts.isIncluded] - counts toward the subtotal
 *   (default: every non-Option line)
 * @param {(line) => boolean} [opts.isEligible] - extra eligibility predicate
 *   (the change order passes "status is added/changed")
 * @param {object} [opts.quote] - quote meta for isQuoteRepriceable
 * @param {boolean} [opts.preferStamp]
 * @param {number} [opts.offsetCents=0] - added to the line sum to form the
 *   figure (a change order's net change is revised - original: offset is
 *   -originalTotal)
 * @param {(line, newTotal, index) => object} [opts.writeTotal] - how to
 *   write a new total onto a line (default: lineTotal + line_total +
 *   marginSource:'price_override')
 * @returns {{ ok: true, lines: Array, changes: Array<{index:number,total:number}> }
 *          | { ok: false, reason: string, message: string, minCents?: number, maxCents?: number }}
 */
export function distributeToTarget(lines, targetSubtotalCents, opts = {}) {
  const safe = Array.isArray(lines) ? lines : [];
  const target = Math.round(Number(targetSubtotalCents));
  if (!Number.isFinite(target)) {
    return { ok: false, reason: TARGET_REFUSALS.INVALID, message: 'Enter a number.' };
  }

  const editability = targetEditability(safe, opts);
  if (!editability.editable) {
    return { ok: false, reason: editability.reason, message: editability.message };
  }

  const measure = typeof opts.measure === 'function'
    ? opts.measure
    : (li) => computeLineItem(li, opts.marginContext ?? 0);
  const isIncluded = typeof opts.isIncluded === 'function' ? opts.isIncluded : (li) => !isOptionTargetLine(li);
  const writeTotal = typeof opts.writeTotal === 'function' ? opts.writeTotal : defaultWrite;
  const offset = Math.round(Number(opts.offsetCents) || 0);

  let fixed = offset;
  let eligibleSum = 0;
  const eligible = []; // { index, total, cost }
  safe.forEach((li, index) => {
    if (!li || !isIncluded(li)) return;
    const m = measure(li, index) || {};
    const total = Math.round(Number(m.total) || 0);
    const cost = Math.round(Number(m.cost) || 0);
    if (lineIneligibility(li, opts) === null && total > 0) {
      eligible.push({ index, total, cost });
      eligibleSum += total;
    } else {
      fixed += total;
    }
  });

  if (eligible.length === 0 || eligibleSum <= 0) {
    return {
      ok: false,
      reason: TARGET_REFUSALS.NO_ELIGIBLE_TOTAL,
      message: 'The lines that can be re-priced have no price yet. Add costs to them first.',
    };
  }

  const current = fixed + eligibleSum;
  if (target === current) {
    return { ok: true, lines: safe, changes: [], unchanged: true };
  }

  // Reachable range: every eligible line must stay at or above cost (margin
  // >= 0) and at or below the engine's margin clamp.
  let sMin = 0;
  let sMax = Infinity;
  eligible.forEach(({ total, cost }) => {
    sMin = Math.max(sMin, cost / total);
    if (cost > 0) sMax = Math.min(sMax, cost / ((1 - MAX_MARGIN_PCT) * total));
  });
  // Epsilon: cost/total * total is not always exact in floating point.
  const minCents = fixed + Math.ceil(sMin * eligibleSum - 1e-6);
  const maxCents = Number.isFinite(sMax) ? fixed + Math.floor(sMax * eligibleSum + 1e-6) : null;
  const range = maxCents != null
    ? `${fmtCents(minCents)} to ${fmtCents(maxCents)}`
    : `at least ${fmtCents(minCents)}`;

  const need = target - fixed;
  const scale = need / eligibleSum;
  const next = eligible.map((e) => ({ ...e, newTotal: Math.round(e.total * scale) }));
  const residual = need - next.reduce((s, e) => s + e.newTotal, 0);
  if (residual !== 0) {
    let largest = next[0];
    next.forEach((e) => { if (e.newTotal > largest.newTotal) largest = e; });
    largest.newTotal += residual;
  }

  const belowCost = next.some((e) => e.newTotal < e.cost || e.newTotal <= 0);
  const aboveMax = next.some((e) => e.cost > 0 && e.newTotal > 0 && marginFromPrice(e.cost, e.newTotal) > MAX_MARGIN_PCT + 1e-9);
  if (belowCost || aboveMax) {
    return {
      ok: false,
      reason: belowCost ? TARGET_REFUSALS.BELOW_COST : TARGET_REFUSALS.ABOVE_MAX,
      message: belowCost
        ? `That would price a line below its cost. Reachable: ${range}.`
        : `That would push a line's margin above ${Math.round(MAX_MARGIN_PCT * 100)}%. Reachable: ${range}.`,
      minCents,
      maxCents,
    };
  }

  const byIndex = new Map(next.map((e) => [e.index, e.newTotal]));
  const out = safe.map((li, i) => (byIndex.has(i) ? writeTotal(li, byIndex.get(i), i) : li));
  return {
    ok: true,
    lines: out,
    changes: next.map((e) => ({ index: e.index, total: e.newTotal })),
    minCents,
    maxCents,
  };
}

/**
 * Convert a Gross Margin % target into the subtotal it implies against the
 * WHOLE-quote cost the card's margin is measured on.
 * Returns null when there is no cost to measure against or gm is out of range.
 */
export function subtotalForMargin(costCents, gm) {
  const cost = Number(costCents);
  const g = Number(gm);
  if (!Number.isFinite(cost) || cost === 0 || !Number.isFinite(g)) return null;
  if (g < 0 || g >= 1) return null;
  return Math.round(cost / (1 - g));
}

export default distributeToTarget;
